import React, { useEffect, useState, useMemo } from "react";
import { ExpandMore, ExpandLess } from "@mui/icons-material";
import { motion } from "framer-motion";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import axios from "axios";
import dayjs from "dayjs";
import { ChevronLeft, ChevronRight, Calendar, Users, AlertTriangle, CheckCircle, XCircle } from "lucide-react";
import { fetchAllUsers } from "../../../../redux/authSlice";
import { listenToShortstayReservaionData, listenToListingReservations } from "../../../../redux/shortstayReservationSlice";
import { listenToShortstayData } from "../../../../redux/shortstaysSlice";
import { AppDispatch, RootState } from "../../../../redux/store";

/* ── reschedule modal tokens (dark) ── */
const R = {
  bg:          "#141414",
  surface:     "#1f1f1f",
  surface2:    "#2a2a2a",
  border:      "#2a2a2a",
  border2:     "#3a3a3a",
  text:        "#e5e5e5",
  textSoft:    "#a3a3a3",
  muted:       "#737373",
  red:         "#e50914",
  redLight:    "rgba(229,9,20,0.12)",
  redBorder:   "rgba(229,9,20,0.35)",
  gold:        "#f5c518",
  goldLight:   "rgba(245,197,24,0.12)",
  goldBorder:  "rgba(245,197,24,0.35)",
  green:       "#46d369",
  greenLight:  "rgba(70,211,105,0.12)",
  greenBorder: "rgba(70,211,105,0.35)",
  gray:        "#404040",
  sans:        "'Montserrat', sans-serif",
  serif:       "'Cormorant Garamond', serif",
};

const FONT_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@300;400;500;600&family=Montserrat:wght@300;400;500;600;700&display=swap');
  * { box-sizing: border-box; }
  ::-webkit-scrollbar { width: 4px; }
  ::-webkit-scrollbar-track { background: ${R.surface}; }
  ::-webkit-scrollbar-thumb { background: ${R.border2}; border-radius: 4px; }
`;

/* ── helpers ── */
const normalizeDateHelper = (date: any): Date | null => {
  if (!date) return null;
  if (date.seconds) return new Date(date.seconds * 1000);
  return new Date(date);
};

const formatDateHelper = (date: any): string => {
  const d = normalizeDateHelper(date);
  if (!d || isNaN(d.getTime())) return "—";
  return new Date(d.getFullYear(), d.getMonth(), d.getDate())
    .toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
};

const toDateKey = (date: any): string => {
  const d = normalizeDateHelper(date);
  if (!d || isNaN(d.getTime())) return "";
  return d.toISOString().slice(0, 10);
};

/* ══════════════════════════════════════════════════ */
export default function CancelShortAccomodationBooking() {
  const [open, setOpen] = useState(false);

  const { guestId: guestIdParam, listingId, reservationId } = useParams<{
    guestId: string;
    listingId: string;
    reservationId: string;
  }>();

  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const { user }            = useSelector((state: RootState) => state.auth);
  const listing             = useSelector((state: RootState) => state.shortstays.currentListing);
  const reservation         = useSelector((state: RootState) => state.shortstayReservations.reservationById);
  const allUsers            = useSelector((state: RootState) => state.auth.allUsers);
  const listingReservations = useSelector((state: any) => state.shortstayReservations.listingReservations);

  const [showModal,           setShowModal]           = useState(false);
  const [cancellationReason,  setCancellationReason]  = useState("");
  const [loading,             setLoading]             = useState(false);

  /* ── reschedule state ── */
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [rescheduleLoading,   setRescheduleLoading]   = useState(false);
  const [rescheduleError,     setRescheduleError]     = useState("");
  const [rescheduleSuccess,   setRescheduleSuccess]   = useState(false);
  const [reschedulePreview,   setReschedulePreview]   = useState<any>(null);
  const [previewLoading,      setPreviewLoading]      = useState(false);

  const today = dayjs();
  const [cursor,      setCursor]      = useState(dayjs().startOf("month"));
  const [newCheckIn,  setNewCheckIn]  = useState<string | null>(null);
  const [newCheckOut, setNewCheckOut] = useState<string | null>(null);
  const [guestCount,  setGuestCount]  = useState({ adults: 1, children: 0, infants: 0, pets: 0 });

  const guestId = guestIdParam || user?.userId || "";

  useEffect(() => {
    if (listingId)     dispatch(listenToShortstayData(listingId));
    if (reservationId) dispatch(listenToShortstayReservaionData(reservationId));
    if (listingId)     dispatch(listenToListingReservations(listingId));
    if (allUsers.length === 0) dispatch(fetchAllUsers());
  }, [dispatch, listingId, reservationId, allUsers.length]);

  useEffect(() => {
    if (!reservation) return;
    setGuestCount({
      adults:   reservation.adults   || 1,
      children: reservation.children || 0,
      infants:  reservation.infants  || 0,
      pets:     reservation.pets     || 0,
    });
    const ci = toDateKey(reservation.checkIn);
    if (ci) setCursor(dayjs(ci).startOf("month"));
  }, [reservation]);

  const booking      = reservation;
  const isCancelled  = reservation?.isCancelled === true;

  const currentCheckIn  = toDateKey(reservation?.checkIn);
  const currentCheckOut = toDateKey(reservation?.checkOut);
  const currentDays     = reservation?.reservedDuration || 0;

  /* ── blocked dates ── */
  const blockedDates = useMemo(() => {
    const s = new Set<string>();
    listingReservations
      .filter((r: any) => r.id !== reservationId && !r.isCancelled)
      .forEach((r: any) => {
        let d = dayjs(toDateKey(r.checkIn));
        const end = dayjs(toDateKey(r.checkOut));
        while (d.isBefore(end, "day")) { s.add(d.format("YYYY-MM-DD")); d = d.add(1, "day"); }
      });
    listing?.unavailableDates?.forEach((d: string) => s.add(d));
    return s;
  }, [listingReservations, listing, reservationId]);

  /* ── current res date set ── */
  const currentResDateSet = useMemo(() => {
    const s = new Set<string>();
    if (!currentCheckIn || !currentCheckOut) return s;
    let d = dayjs(currentCheckIn);
    while (d.isBefore(dayjs(currentCheckOut), "day")) { s.add(d.format("YYYY-MM-DD")); d = d.add(1, "day"); }
    return s;
  }, [currentCheckIn, currentCheckOut]);

  const canGoPrev = cursor.isAfter(today.startOf("month"), "month");

  /* ── date click ── */
  const handleDateClick = (dateStr: string) => {
    if (blockedDates.has(dateStr)) return;
    if (dayjs(dateStr).isBefore(today, "day")) return;
    setRescheduleError("");

    if (!newCheckIn || (newCheckIn && newCheckOut)) {
      setNewCheckIn(dateStr); setNewCheckOut(null); setReschedulePreview(null); return;
    }
    if (dayjs(dateStr).isBefore(dayjs(newCheckIn), "day")) {
      setNewCheckIn(dateStr); setNewCheckOut(null); return;
    }
    const days = dayjs(dateStr).diff(dayjs(newCheckIn), "day");
    if (days < currentDays) {
      setRescheduleError(`Must be at least ${currentDays} night${currentDays > 1 ? "s" : ""}`);
      return;
    }
    let d = dayjs(newCheckIn).add(1, "day");
    while (d.isBefore(dayjs(dateStr), "day")) {
      if (blockedDates.has(d.format("YYYY-MM-DD"))) {
        setRescheduleError("Range includes unavailable dates"); return;
      }
      d = d.add(1, "day");
    }
    setNewCheckOut(dateStr);
  };

  /* ── price preview ── */
  useEffect(() => {
    if (!newCheckIn || !newCheckOut || !reservationId || !listingId || !listing?.hostId) return;
    const run = async () => {
      setPreviewLoading(true);
      try {
        const res = await axios.post(
          "https://europe-west3-alreluxe.cloudfunctions.net/createRescheduleRequest",
          { reservationId, guestId, hostId: listing.hostId, listingId, newCheckIn, newCheckOut, dryRun: true }
        );
        setReschedulePreview(res.data);
      } catch { setReschedulePreview(null); }
      finally { setPreviewLoading(false); }
    };
    run();
  }, [newCheckIn, newCheckOut]);

  /* ── submit reschedule ── */
  const handleRescheduleSubmit = async () => {
    if (!newCheckIn || !newCheckOut) return;
    setRescheduleLoading(true); setRescheduleError("");
    try {
      await axios.post(
        "https://europe-west3-alreluxe.cloudfunctions.net/createRescheduleRequest",
        { reservationId, guestId, hostId: listing?.hostId, listingId, newCheckIn, newCheckOut }
      );
      setRescheduleSuccess(true);
    } catch (err: any) {
      setRescheduleError(err?.response?.data?.error || "Failed to send request");
    } finally { setRescheduleLoading(false); }
  };

  /* ── cancel submit ── */
  const confirmCancel = async () => {
    if (isCancelled) return;
    if (!booking || !listing || !guestId) { alert("Missing information."); return; }
    setLoading(true);
    try {
      await axios.post(
        "https://europe-west3-alreluxe.cloudfunctions.net/applyShortRefund",
        {
          reservationId: booking.id, hostId: listing.hostId,
          guestId, listingId, dateKey: booking.checkIn,
          cancellationType: "guest", cancellationReason,
        }
      );
      navigate(`/shortaccomodationbookings/${guestId}`);
    } catch (error: any) {
      alert(error?.response?.data?.message || "Failed to cancel booking. Please try again.");
    } finally { setLoading(false); }
  };

  /* ── original date formatter ── */
  const normalizeDate = (date: any) => {
    if (!date) return "-";
    if (date.seconds) return new Date(date.seconds * 1000);
    return new Date(date);
  };

  const formatDate = (date: any) => {
    const d = normalizeDate(date);
    if (!(d instanceof Date) || isNaN(d.getTime())) return "-";
    const normalized = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    return normalized.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  };

  const newDays = newCheckIn && newCheckOut
    ? dayjs(newCheckOut).diff(dayjs(newCheckIn), "day") : 0;

  /* ── calendar renderer ── */
  const renderCalendar = () => {
    const start       = cursor.startOf("month");
    const matrixStart = start.subtract(start.day(), "day");
    const matrix      = Array.from({ length: 42 }, (_, i) => matrixStart.add(i, "day"));

    return (
      <div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
          <button
            onClick={() => canGoPrev && setCursor(c => c.subtract(1, "month"))}
            disabled={!canGoPrev}
            style={{
              width: 30, height: 30, borderRadius: "50%",
              border: `1px solid ${R.border2}`, background: R.surface2,
              display: "grid", placeItems: "center", cursor: canGoPrev ? "pointer" : "not-allowed",
              color: R.text, opacity: canGoPrev ? 1 : 0.3,
            }}
          >
            <ChevronLeft size={14} />
          </button>
          <span style={{ fontFamily: R.serif, fontSize: 17, color: R.text, letterSpacing: "0.02em" }}>
            {cursor.format("MMMM YYYY")}
          </span>
          <button
            onClick={() => setCursor(c => c.add(1, "month"))}
            style={{
              width: 30, height: 30, borderRadius: "50%",
              border: `1px solid ${R.border2}`, background: R.surface2,
              display: "grid", placeItems: "center", cursor: "pointer", color: R.text,
            }}
          >
            <ChevronRight size={14} />
          </button>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", marginBottom: 8 }}>
          {["Su","Mo","Tu","We","Th","Fr","Sa"].map(d => (
            <div key={d} style={{ textAlign: "center", fontSize: 10, fontWeight: 600, color: R.muted, letterSpacing: "0.08em" }}>
              {d}
            </div>
          ))}
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", gap: 3 }}>
          {matrix.map((d, i) => {
            const key          = d.format("YYYY-MM-DD");
            const isOtherMo    = d.month() !== cursor.month();
            const isPast       = d.isBefore(today, "day");
            const isBlocked    = blockedDates.has(key);
            const isCurRes     = currentResDateSet.has(key);
            const isCurCI      = key === currentCheckIn;
            const isCurCO      = key === currentCheckOut;
            const isNewCI      = key === newCheckIn;
            const isNewCO      = key === newCheckOut;
            const isInNewRange = newCheckIn && newCheckOut &&
              dayjs(key).isAfter(dayjs(newCheckIn)) && dayjs(key).isBefore(dayjs(newCheckOut));

            let bg = "transparent", color = R.text, border = "1px solid transparent";
            let op = isOtherMo ? 0.15 : 1, cur = "pointer";

            if (isPast || isOtherMo) {
              color = R.muted; cur = "not-allowed"; op = isOtherMo ? 0.15 : 0.35;
            } else if (isBlocked) {
              bg = R.surface2; color = R.muted; cur = "not-allowed"; border = `1px solid ${R.border}`;
            } else if (isCurCI || isCurCO) {
              bg = R.red; color = "#fff"; border = `1px solid #b81d24`;
            } else if (isCurRes) {
              bg = R.redLight; color = "#ff6b6b"; border = `1px solid ${R.redBorder}`;
            } else if (isNewCI || isNewCO) {
              bg = R.gold; color = "#000"; border = `1px solid ${R.gold}`;
            } else if (isInNewRange) {
              bg = R.goldLight; color = R.gold; border = `1px solid ${R.goldBorder}`;
            }

            return (
              <div
                key={i}
                onClick={() => !isPast && !isBlocked && !isOtherMo && handleDateClick(key)}
                style={{
                  height: 36, borderRadius: 6,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 13, fontWeight: isNewCI || isNewCO || isCurCI || isCurCO ? 700 : 400,
                  background: bg, color, border, opacity: op, cursor: cur,
                  transition: "all .1s",
                }}
              >
                {d.date()}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  /* ════════════════════════════════ RENDER ════════════════════════════════ */
  return (
    <div style={styles.centerWrap}>
      <style>{FONT_CSS}</style>
      <aside style={styles.stickyCard}>

        {/* 🏠 LISTING SUMMARY — original */}
        <section style={styles.listingCard}>
          <div style={styles.listingLeft}>
            <strong style={styles.listingTitle}>
              {listing?.title || "Unnamed Listing"}
            </strong>
          </div>
          <div style={styles.listingImageWrap}>
            {listing?.photos?.[0] ? (
              <img src={listing.photos[0]} alt="Listing" style={styles.listingImage} />
            ) : (
              <div style={styles.imagePlaceholder} />
            )}
          </div>
        </section>

        {/* 📅 INPUT CARD — original */}
        <div style={styles.inputCard}>
          <div style={styles.grid2}>
            <div style={styles.inputCell}>
              <div style={styles.inputLabel}>CHECK-IN</div>
              <div style={styles.inputValue}>{booking ? formatDate(booking.checkIn) : "-"}</div>
            </div>
            <div style={{ ...styles.inputCell, borderLeft: "1px solid #ddd" }}>
              <div style={styles.inputLabel}>CHECKOUT</div>
              <div style={styles.inputValue}>{booking ? formatDate(booking.checkOut) : "-"}</div>
            </div>
          </div>
          <div style={styles.guestRow}>
            <div style={styles.inputLabel}>DURATION</div>
            <div style={styles.inputValue}>
              {booking?.reservedDuration
                ? `${booking.reservedDuration} night${booking.reservedDuration > 1 ? "s" : ""}`
                : "-"}
            </div>
          </div>
        </div>

        <div style={{ flex: 1 }} />

        {/* ── RESCHEDULE BUTTON (new, above cancel) ── 
        
          {!isCancelled && (
          <button
            style={{
              ...styles.reserveBtn,
              background: "#C9A227",
              marginBottom: 8,
              display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
            }}
            onClick={() => {
              setShowRescheduleModal(true);
              setRescheduleSuccess(false);
              setReschedulePreview(null);
              setNewCheckIn(null);
              setNewCheckOut(null);
              setRescheduleError("");
            }}
          >
            <Calendar size={16} />
            RESCHEDULE BOOKING
          </button>
        )}
        
        */}
      

        {/* ── CANCEL BUTTON — original ── */}
        <button
          style={styles.reserveBtn}
          onClick={() => setShowModal(true)}
          disabled={loading}
        >
          {loading ? "Processing..." : "CANCEL BOOKING"}
        </button>

        {/* ℹ️ NOTE — original */}
        <section style={styles.card}>
          <div style={styles.mutedText}>
            Read Refund Policy before cancelling. If you cancel, you will lose your service fee and one night's rate will be paid to the host if it's system policy. The remaining balance will be refunded.
          </div>
        </section>

        {/* 📕 PAYMENT DETAILS — original */}
        <div style={styles.bookCard}>
          <div style={styles.bookHeader} onClick={() => setOpen(o => !o)}>
            <strong>Payment details</strong>
            {open ? <ExpandLess /> : <ExpandMore />}
          </div>
          <motion.div
            initial={false}
            animate={{ height: open ? "auto" : 0, opacity: open ? 1 : 0 }}
            transition={{ duration: 0.4, ease: "easeInOut" }}
            style={styles.whitePage}
          >
            <div style={styles.pageTop} />
            <div style={styles.pageContent}>
              <div style={styles.sectionHeader}>Payment Summary</div>
              <div style={styles.tableRow}>
                <span>You Paid</span>
                <span>€{(booking?.totalPaid || 0).toFixed(2)}</span>
              </div>
              <div style={styles.sectionHeader}>You Will Lose</div>
              <div style={styles.tableRow}>
                <span>Service Fee</span>
                <span>€{(booking?.guestsFee || 0).toFixed(2)}</span>
              </div>
              <div style={styles.tableRow}>
                <span>VAT Collected</span>
                <span>€{(booking?.vatCollected || 0).toFixed(2)}</span>
              </div>
              <div style={{ ...styles.tableRow, fontWeight: 900, marginTop: 10, borderTop: "2px solid #eee", paddingTop: 10 }}>
                <span>Estimated Refund</span>
                <span>€{Math.max(0,
                  (booking?.totalPaid || 0) -
                  (booking?.guestsFee || 0) -
                  (booking?.vatCollected || 0)
                ).toFixed(2)}</span>
              </div>
            </div>
          </motion.div>
        </div>

        {/* ════════ CANCEL MODAL — original style ════════ */}
        {showModal && !isCancelled && (
          <div style={styles.floatingOverlay}>
            <div style={styles.floatingCard}>
              <h3 style={{ marginBottom: 14 }}>Confirm Cancellation</h3>
              <div style={styles.warningBox}>
                ⚠️ If you cancel, you will lose your <strong>service fee</strong> and
                <strong> one night's rate</strong> will be paid to the host.
                The remaining balance will be refunded.
              </div>
              <textarea
                placeholder="Optional: tell us why you're cancelling"
                value={cancellationReason}
                onChange={e => setCancellationReason(e.target.value)}
                style={styles.modalTextarea}
              />
              <div style={styles.modalButtonRow}>
                <button
                  style={{
                    ...styles.reserveBtn,
                    background: isCancelled ? "#ccc" : "#cc0000",
                    cursor: isCancelled ? "not-allowed" : "pointer",
                    opacity: isCancelled ? 0.7 : 1,
                  }}
                  onClick={confirmCancel}
                  disabled={loading || isCancelled}
                >
                  {isCancelled ? "ALREADY CANCELLED" : loading ? "Processing..." : "CONFIRM CANCELLATION"}
                </button>
                <button onClick={() => setShowModal(false)} disabled={loading} style={styles.modalCancelBtn}>
                  Go Back
                </button>
              </div>
            </div>
          </div>
        )}

      </aside>

      {/* ════════ RESCHEDULE MODAL — dark Netflix style ════════ */}
      {showRescheduleModal && (
        <div
          style={{
            position: "fixed", inset: 0,
            background: "rgba(0,0,0,0.75)", backdropFilter: "blur(8px)",
            display: "flex", justifyContent: "center", alignItems: "center",
            zIndex: 1000, padding: 16,
          }}
          onClick={() => !rescheduleLoading && setShowRescheduleModal(false)}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.2 }}
            onClick={e => e.stopPropagation()}
            style={{
              background: R.bg, width: "100%", maxWidth: 520,
              maxHeight: "92vh", overflowY: "auto",
              borderRadius: 18,
              boxShadow: "0 30px 80px rgba(0,0,0,.8)",
              border: `1px solid ${R.border2}`,
            }}
          >
            {/* gold top bar */}
            <div style={{ height: 4, background: "#C9A227", borderRadius: "18px 18px 0 0" }} />

            {/* header */}
            <div style={{
              display: "flex", justifyContent: "space-between", alignItems: "center",
              padding: "18px 22px 14px",
              borderBottom: `1px solid ${R.border}`,
              position: "sticky", top: 0, background: R.bg, zIndex: 10,
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <Calendar size={17} color="#C9A227" />
                <span style={{ fontFamily: R.serif, fontSize: 20, color: R.text, fontWeight: 500 }}>
                  Reschedule Booking
                </span>
              </div>
              <button
                onClick={() => !rescheduleLoading && setShowRescheduleModal(false)}
                style={{
                  background: R.surface2, border: `1px solid ${R.border}`,
                  borderRadius: "50%", width: 30, height: 30,
                  display: "grid", placeItems: "center",
                  cursor: "pointer", color: R.textSoft, fontSize: 14,
                }}
              >✕</button>
            </div>

            <div style={{ padding: "18px 22px", display: "flex", flexDirection: "column", gap: 16 }}>

              {rescheduleSuccess ? (
                <div style={{ textAlign: "center", padding: "32px 0" }}>
                  <div style={{
                    width: 60, height: 60, borderRadius: "50%",
                    background: R.greenLight, border: `2px solid ${R.greenBorder}`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    margin: "0 auto 16px",
                  }}>
                    <CheckCircle size={26} color={R.green} />
                  </div>
                  <div style={{ fontFamily: R.serif, fontSize: 22, color: R.text, marginBottom: 8 }}>Request Sent!</div>
                  <p style={{ fontSize: 13, color: R.muted, lineHeight: 1.7, marginBottom: 24 }}>
                    Your reschedule request is with the host. You'll be notified once they respond.
                  </p>
                  <button
                    onClick={() => setShowRescheduleModal(false)}
                    style={{
                      padding: "12px 32px", borderRadius: 10,
                      background: "#C9A227", border: "none",
                      color: "#000", fontWeight: 700, fontSize: 14,
                      cursor: "pointer",
                    }}
                  >Done</button>
                </div>
              ) : (
                <>
                  {/* current booking info */}
                  <div style={{
                    padding: "10px 14px", borderRadius: 10,
                    background: R.redLight, border: `1px solid ${R.redBorder}`,
                    fontSize: 12, color: "#ff8080", lineHeight: 1.6,
                    display: "flex", gap: 8,
                  }}>
                    <AlertTriangle size={13} style={{ flexShrink: 0, marginTop: 2 }} />
                    <div>
                      <strong style={{ color: "#ffaaaa" }}>Current:</strong>{" "}
                      {formatDateHelper(reservation?.checkIn)} → {formatDateHelper(reservation?.checkOut)} ({currentDays}N)
                      {" · "}New must be <strong style={{ color: "#ffaaaa" }}>≥ {currentDays}N</strong>
                    </div>
                  </div>

                  {/* legend */}
                  <div style={{ display: "flex", gap: 14, flexWrap: "wrap" as const }}>
                    {[
                      { color: R.red,  label: "Your booking" },
                      { color: R.gray, label: "Unavailable" },
                      { color: "#C9A227", label: "New selection" },
                    ].map(({ color, label }) => (
                      <div key={label} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: R.muted }}>
                        <div style={{ width: 10, height: 10, borderRadius: 3, background: color }} />
                        {label}
                      </div>
                    ))}
                  </div>

                  {/* calendar */}
                  <div style={{
                    background: R.surface, borderRadius: 12, padding: "16px 14px",
                    border: `1px solid ${R.border}`,
                  }}>
                    {renderCalendar()}
                  </div>

                  {/* selected range */}
                  {newCheckIn && (
                    <div style={{
                      padding: "10px 14px", borderRadius: 10,
                      background: "rgba(201,162,39,0.12)", border: "1px solid rgba(201,162,39,0.35)",
                      fontSize: 13, color: "#C9A227", fontWeight: 600,
                      display: "flex", alignItems: "center", gap: 8,
                    }}>
                      <Calendar size={13} />
                      {!newCheckOut
                        ? `${dayjs(newCheckIn).format("DD MMM YYYY")} — select check-out`
                        : `${dayjs(newCheckIn).format("DD MMM")} → ${dayjs(newCheckOut).format("DD MMM YYYY")} · ${newDays}N`}
                    </div>
                  )}

                  {/* error */}
                  {rescheduleError && (
                    <div style={{
                      padding: "10px 14px", borderRadius: 10,
                      background: R.redLight, border: `1px solid ${R.redBorder}`,
                      fontSize: 12, color: "#ff8080",
                      display: "flex", alignItems: "center", gap: 8,
                    }}>
                      <XCircle size={13} /> {rescheduleError}
                    </div>
                  )}

                  {/* price preview */}
                  {(previewLoading || reschedulePreview) && newCheckOut && (
                    <div style={{ borderRadius: 12, border: `1px solid ${R.border}`, background: R.surface, overflow: "hidden" }}>
                      <div style={{
                        padding: "9px 14px", background: R.surface2,
                        fontSize: 10, fontWeight: 700, letterSpacing: "0.1em",
                        color: R.muted, textTransform: "uppercase" as const,
                      }}>
                        Pricing Preview
                      </div>
                      {previewLoading ? (
                        <div style={{ padding: "14px", color: R.muted, fontSize: 13 }}>Calculating…</div>
                      ) : (
                        <div style={{ padding: "12px 14px" }}>
                          {[
                            { label: "Extra nights",    val: reschedulePreview.extraDays > 0 ? `+${reschedulePreview.extraDays}` : "None" },
                            { label: "Additional cost", val: reschedulePreview.totalToCharge > 0 ? `€${reschedulePreview.totalToCharge.toFixed(2)}` : "Free" },
                          ].map(({ label, val }) => (
                            <div key={label} style={{
                              display: "flex", justifyContent: "space-between",
                              fontSize: 13, padding: "6px 0", borderBottom: `1px solid ${R.border}`,
                            }}>
                              <span style={{ color: R.muted }}>{label}</span>
                              <span style={{ fontWeight: 600, color: R.text }}>{val}</span>
                            </div>
                          ))}
                          <div style={{ marginTop: 8, fontSize: 11, color: R.muted, lineHeight: 1.6 }}>
                            {reschedulePreview.requiresPayment
                              ? "💳 Extra nights charged to saved card after host approval."
                              : reschedulePreview.rescheduleFee > 0
                              ? `Reschedule fee: €${reschedulePreview.rescheduleFee.toFixed(2)}`
                              : "✓ No additional charge."}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* guests */}
                  <div style={{ borderRadius: 12, border: `1px solid ${R.border}`, background: R.surface, overflow: "hidden" }}>
                    <div style={{
                      padding: "9px 14px", background: R.surface2,
                      fontSize: 10, fontWeight: 700, letterSpacing: "0.1em",
                      color: R.muted, textTransform: "uppercase" as const,
                      display: "flex", alignItems: "center", gap: 6,
                    }}>
                      <Users size={11} /> Guests
                    </div>
                    <div style={{ padding: "0 14px" }}>
                      {[
                        { key: "adults",   label: "Adults",   sub: "Age 13+",   min: 1 },
                        { key: "children", label: "Children", sub: "Ages 2–12", min: 0 },
                        { key: "infants",  label: "Infants",  sub: "Under 2",   min: 0 },
                        { key: "pets",     label: "Pets",     sub: "Animals",   min: 0 },
                      ].map(({ key, label, sub, min }, idx, arr) => (
                        <div key={key} style={{
                          display: "flex", justifyContent: "space-between", alignItems: "center",
                          padding: "10px 0",
                          borderBottom: idx < arr.length - 1 ? `1px solid ${R.border}` : "none",
                        }}>
                          <div>
                            <div style={{ fontSize: 13, fontWeight: 500, color: R.text }}>{label}</div>
                            <div style={{ fontSize: 11, color: R.muted }}>{sub}</div>
                          </div>
                          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                            <button
                              onClick={() => setGuestCount(p => ({ ...p, [key]: Math.max(min, (p as any)[key] - 1) }))}
                              disabled={(guestCount as any)[key] <= min}
                              style={{
                                width: 28, height: 28, borderRadius: "50%",
                                border: `1px solid ${(guestCount as any)[key] <= min ? R.border : R.border2}`,
                                background: R.surface2, fontSize: 16,
                                cursor: (guestCount as any)[key] <= min ? "not-allowed" : "pointer",
                                color: (guestCount as any)[key] <= min ? R.muted : R.text,
                                display: "grid", placeItems: "center",
                              }}
                            >−</button>
                            <span style={{ fontSize: 13, fontWeight: 600, color: R.text, minWidth: 14, textAlign: "center" as const }}>
                              {(guestCount as any)[key]}
                            </span>
                            <button
                              onClick={() => setGuestCount(p => ({ ...p, [key]: (p as any)[key] + 1 }))}
                              style={{
                                width: 28, height: 28, borderRadius: "50%",
                                border: `1px solid ${R.border2}`,
                                background: R.surface2, fontSize: 16,
                                cursor: "pointer", color: R.text,
                                display: "grid", placeItems: "center",
                              }}
                            >+</button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* submit */}
                  <button
                    onClick={handleRescheduleSubmit}
                    disabled={!newCheckIn || !newCheckOut || rescheduleLoading}
                    style={{
                      width: "100%", padding: "14px 0", borderRadius: 10, border: "none",
                      background: !newCheckIn || !newCheckOut ? R.surface2
                        : rescheduleLoading ? "#555" : "#C9A227",
                      color: !newCheckIn || !newCheckOut ? R.muted : "#000",
                      fontWeight: 700, fontSize: 15,
                      cursor: !newCheckIn || !newCheckOut || rescheduleLoading ? "not-allowed" : "pointer",
                      fontFamily: R.sans,
                    }}
                  >
                    {rescheduleLoading ? "Sending…"
                      : !newCheckIn || !newCheckOut ? "Select new dates first"
                      : "Send Reschedule Request"}
                  </button>

                  <p style={{ fontSize: 11, color: R.muted, textAlign: "center" as const, margin: 0 }}>
                    Host must approve before changes take effect.
                  </p>
                </>
              )}
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}

/* ───────────── ORIGINAL STYLES (unchanged) ───────────── */
const styles: Record<string, React.CSSProperties> = {
  centerWrap: { display: "flex", justifyContent: "center", padding: "40px 16px" },
  stickyCard: {
    width: "100%", maxWidth: 520, minWidth: 340,
    border: "1px solid #eee", borderRadius: 18, padding: 24,
    boxShadow: "0 10px 30px rgba(0,0,0,.12)",
    display: "flex", flexDirection: "column", minHeight: 440, background: "#fff",
  },
  card: { padding: 16, borderRadius: 16, border: "1px solid #eee", marginBottom: 12 },
  mutedText: { fontSize: 13, color: "#666" },
  inputCard: { border: "1px solid #ddd", borderRadius: 12, overflow: "hidden", marginBottom: 16, background: "#fff" },
  grid2: { display: "grid", gridTemplateColumns: "1fr 1fr" },
  guestRow: { borderTop: "1px solid #ddd", padding: "10px 12px" },
  inputCell: { padding: "12px 14px" },
  inputLabel: { fontSize: 10, fontWeight: 900, letterSpacing: 0.6, color: "#222" },
  inputValue: { fontSize: 14, fontWeight: 600, marginTop: 2, color: "#717171" },
  reserveBtn: {
    width: "100%", padding: 14, background: "#cc0000", border: "none",
    fontWeight: 900, cursor: "pointer", color: "#fff", fontSize: 16, marginBottom: 16,
  },
  listingCard: {
    display: "flex", justifyContent: "space-between", alignItems: "center", gap: 14,
    padding: 16, borderRadius: 16, border: "1px solid #eee", marginBottom: 12, background: "#fff",
  },
  listingLeft: { display: "flex", flexDirection: "column", gap: 6, flex: 1 },
  listingTitle: { fontSize: 15, fontWeight: 900, lineHeight: 1.25, color: "#222" },
  listingAddress: { fontSize: 13, color: "#717171", lineHeight: 1.3 },
  listingImageWrap: { width: 64, height: 64, borderRadius: 12, overflow: "hidden", cursor: "pointer", flexShrink: 0, boxShadow: "0 4px 10px rgba(0,0,0,0.12)" },
  listingImage: { width: "100%", height: "100%", objectFit: "cover" },
  imagePlaceholder: { width: "100%", height: "100%", background: "#f0f0f0" },
  bookCard: {
    background: "#fff", border: "2px solid #C9A227", borderRadius: 20, padding: 24,
    width: "100%", maxWidth: 480,
    boxShadow: "0 10px 25px rgba(0,0,0,.12), inset 0 0 6px rgba(0,0,0,.04)",
  },
  bookHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer" },
  whitePage: { background: "#fff", borderRadius: 14, marginTop: 16, boxShadow: "inset 0 0 8px rgba(0,0,0,.05),0 3px 12px rgba(0,0,0,.25)" },
  pageTop: { height: 4, background: "repeating-linear-gradient(90deg,#ddd 0 10px,#fff 10px 20px)" },
  pageContent: { padding: "16px 20px", background: "repeating-linear-gradient(#fff, #fff 28px, #f7f7f7 29px)" },
  tableRow: { display: "flex", justifyContent: "space-between", fontSize: 14, color: "#222", padding: "8px 0", borderBottom: "1px dashed rgba(0,0,0,0.08)" },
  sectionHeader: { marginTop: 16, marginBottom: 10, fontWeight: 700, color: "#ad1212ff" },
  loading: { background: "#000", color: "#fff", display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh" },
  floatingOverlay: {
    position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)",
    backdropFilter: "blur(6px)", display: "flex", justifyContent: "center",
    alignItems: "center", zIndex: 999,
  },
  floatingCard: {
    background: "#fff", width: "92%", maxWidth: 420, borderRadius: 20,
    padding: 28, boxShadow: "0 25px 60px rgba(0,0,0,.25)",
  },
  warningBox: {
    background: "rgba(255,0,0,0.08)", border: "1px solid rgba(255,0,0,0.25)",
    padding: 14, borderRadius: 12, fontSize: 14, color: "#b00000", marginBottom: 16,
  },
  modalTextarea: { width: "100%", padding: 12, borderRadius: 10, border: "1px solid #ddd", marginBottom: 18, resize: "vertical" },
  modalButtonRow: { display: "flex", gap: 10 },
  modalConfirmBtn: { flex: 1, padding: 12, borderRadius: 12, border: "none", background: "#cc0000", color: "#fff", fontWeight: 700, cursor: "pointer" },
  modalCancelBtn: { flex: 1, padding: 12, borderRadius: 12, border: "1px solid #ddd", background: "#f5f5f5", color: "#222", fontWeight: 600, cursor: "pointer" },
};